import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Navbar } from "@/components/layout/Navbar";
import { MenuOverlay } from "@/components/layout/MenuOverlay";
import { Footer } from "@/components/layout/Footer";
import { FloatingCTA } from "@/components/layout/FloatingCTA";
import { scrollToTop, scrollToElement } from "@/lib/smoothScroll";
import { EASE } from "@/lib/motion";

import { BubbleBackground } from "@/components/common/BubbleBackground";

// Height of the fixed header. Every page is offset by it so nothing scrolls
// underneath an opaque bar.
export const HEADER_HEIGHT = 100;

// Which header variant each route wears, per the design: orange over the home's
// hero and the case/about pages, white on the service pages (25:22). Keeping the
// mapping here means a page never reaches up into the layout to recolour chrome.
//
// Both variants paint an opaque surface, so neither can disappear against the
// content behind it — see the note in Navbar.
const TONE_BY_PATH = [
    { match: (path) => path === "/", tone: "brand" },
    { match: (path) => path.startsWith("/servicos"), tone: "paper" },
    { match: (path) => path.startsWith("/cases"), tone: "brand" },
    { match: (path) => path.startsWith("/sobre"), tone: "brand" },
    // Transparent header over the contact page's bubbly background image
    { match: (path) => path.startsWith("/contato"), tone: "contactBg" },
];

const toneFor = (path) => TONE_BY_PATH.find((entry) => entry.match(path))?.tone ?? "paper";

export const SiteLayout = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    // `key` changes on every navigation, even when the URL is identical. Without
    // it, clicking the same hash link twice did nothing: with only pathname+hash
    // as dependencies the effect never re-ran, so a visitor who had scrolled away
    // and clicked the link again stayed put.
    const { pathname, hash, key } = useLocation();
    const reduceMotion = useReducedMotion();

    // Browsers restore the previous scroll position on reload and on back/forward.
    // On a long page that lands the visitor in the middle of it, and it happens
    // after our own reset, overriding it. We place the visitor ourselves.
    useEffect(() => {
        const previous = window.history.scrollRestoration;
        if (previous) window.history.scrollRestoration = "manual";
        return () => {
            if (previous) window.history.scrollRestoration = previous;
        };
    }, []);

    // Navigating should never leave the overlay open or the page mid-scroll.
    // scrollToTop goes through lenis, which owns the scroll position — a plain
    // window.scrollTo would be overwritten on its next frame.
    //
    // A link carrying a hash (the menu's "O QUE NOSSOS CLIENTES DIZEM" points at
    // /#depoimentos) must land on that section instead. React Router does not
    // scroll to hashes on its own, and this effect used to send every navigation
    // to the top, overriding it. The target is offset by the header so the
    // section's heading is not hidden behind it.
    useEffect(() => {
        setIsMenuOpen(false);

        if (!hash) {
            scrollToTop();
            return undefined;
        }

        // Release any leftover overflow lock
        document.documentElement.style.overflow = "";

        let timer1;
        let timer2;
        const scroll = () => {
            const target = document.querySelector(hash);
            if (target) {
                scrollToElement(target, { offset: HEADER_HEIGHT, duration: 1.1 });
            }
        };

        // Scroll smoothly once the route has mounted, and refine after layout settles
        timer1 = setTimeout(scroll, 100);
        timer2 = setTimeout(scroll, 450);

        return () => {
            clearTimeout(timer1);
            clearTimeout(timer2);
        };
    }, [pathname, hash, key]);

    const isContactPage = pathname.startsWith("/contato");

    return (
        <div
            className={`min-h-screen text-ink ${
                isContactPage ? "relative" : "bg-paper"
            }`}
        >
            {isContactPage && <BubbleBackground />}
            <Navbar
                tone={toneFor(pathname)}
                isMenuOpen={isMenuOpen}
                onToggleMenu={() => setIsMenuOpen((open) => !open)}
            />
            <MenuOverlay isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />

            <main style={{ paddingTop: HEADER_HEIGHT }} className="relative z-10">
                {/* A new route fades in rather than cutting. The key is the
                    pathname alone, never location.key: the menu links to
                    /#depoimentos, and a key that included the hash would replay
                    the whole home page underneath the visitor while lenis is
                    still scrolling them to that section.

                    Opacity only, no translation. Three pages already animate
                    their top block from y: 20 on mount (ServiceHero,
                    FounderPage, ContactPage); adding the container's own travel
                    on top would stack two movements under two clocks. Opacity
                    multiplies without fighting.

                    There is no exit animation and no AnimatePresence, so
                    navigation is never delayed and the scroll reset above keeps
                    running exactly when it does today. */}
                <motion.div
                    key={pathname}
                    initial={reduceMotion ? false : { opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.35, ease: EASE }}
                >
                    <Outlet />
                </motion.div>
            </main>

            {!isContactPage && <Footer />}

            {/* Hidden while the menu is open — the overlay is the navigation at
                that point, and the badge would sit on top of it. Also hidden on contact page. */}
            {!isMenuOpen && !isContactPage && <FloatingCTA />}
        </div>
    );
};
